/* DAILE v2 (dv) — header chrome (sections/dv-header.liquid).
 *
 * Module "dv-header" (DV registry, re-initialised by the theme editor):
 *   1. --header-height on :root = measured height of the floating header
 *      (read by Prestige, daile-pdp.css, daile-customizer.css). Removed on unload.
 *   2. Bag count: <cart-count> text is written by daile-cart.js (updateBadge)
 *      and Prestige (cart:change / cart:refresh / pageshow). We only hide the
 *      badge at 0 and keep the bag link's aria-label in sync ("Open cart (2)").
 *      Opening the drawer is daile-cart.js's job ([data-daile-cart-open]).
 *   3. Fit check above 700px: if the pill content (e.g. a longer menu or a
 *      German / Arabic translation) does not fit, hide "Find your set" first
 *      (.dv-header--no-shop), then switch to the menu button
 *      (.dv-header--collapsed). Below 700px the CSS already does this.
 *   4. Country / language selects submit their localization form on change
 *      (a <noscript> button covers the no-JS case).
 *
 * The menu <dialog>, its trigger, close button, focus return, scroll lock,
 * close-on-link and the <details data-dv-autoclose> dropdown are generic
 * dv.js behaviours — nothing to do here.
 */
(() => {
  'use strict';

  if (!window.DV || typeof window.DV.register !== 'function') return;

  const DV = window.DV;
  const html = document.documentElement;
  const wide = window.matchMedia('(min-width: 701px)');

  DV.register('dv-header', (root) => {
    const header = DV.queryAll(root, '[data-dv-header]')[0];
    if (!header) return undefined;

    const pill = header.querySelector('[data-dv-header-pill]');
    const nav = header.querySelector('[data-dv-header-nav]');
    const bag = header.querySelector('[data-dv-bag]');
    const badge = header.querySelector('[data-dv-bag-count]');
    const cleanups = [];

    /* 1. --header-height ------------------------------------------------ */
    const publishHeight = () => {
      const height = header.getBoundingClientRect().height;
      if (height > 0) html.style.setProperty('--header-height', `${height.toFixed(2)}px`);
    };

    /* 3. fit check ------------------------------------------------------- */
    const fits = () => {
      const style = getComputedStyle(pill);
      const available = pill.clientWidth
        - (parseFloat(style.paddingInlineStart) || 0)
        - (parseFloat(style.paddingInlineEnd) || 0);
      const gap = parseFloat(style.columnGap) || 0;
      let needed = 0;
      let count = 0;
      [...pill.children].forEach((child) => {
        const childStyle = getComputedStyle(child);
        if (childStyle.display === 'none' || childStyle.position === 'absolute') return;
        needed += child.getBoundingClientRect().width;
        count += 1;
      });
      needed += gap * Math.max(0, count - 1);
      return needed <= available + 0.5;
    };

    const fit = () => {
      if (!pill) return;
      header.classList.remove('dv-header--no-shop', 'dv-header--collapsed');
      if (!wide.matches || !nav) return;
      if (fits()) return;
      header.classList.add('dv-header--no-shop');
      if (fits()) return;
      header.classList.add('dv-header--collapsed');
    };

    let frame = 0;
    const schedule = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        fit();
        publishHeight();
      });
    };

    if ('ResizeObserver' in window) {
      const observer = new ResizeObserver(schedule);
      observer.observe(header);
      if (pill) observer.observe(pill);
      cleanups.push(() => observer.disconnect());
    } else {
      window.addEventListener('resize', schedule);
      cleanups.push(() => window.removeEventListener('resize', schedule));
    }
    cleanups.push(DV.onMediaChange(wide, schedule));
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(schedule).catch(() => {});
    fit();
    publishHeight();

    /* 2. bag count ------------------------------------------------------- */
    if (badge) {
      const label = bag ? bag.getAttribute('data-dv-label') || '' : '';
      const syncBadge = () => {
        const count = Number.parseInt((badge.textContent || '').trim(), 10) || 0;
        badge.hidden = count <= 0;
        if (bag && label) bag.setAttribute('aria-label', count > 0 ? `${label} (${count})` : label);
      };
      const badgeObserver = new MutationObserver(syncBadge);
      badgeObserver.observe(badge, { childList: true, characterData: true, subtree: true });
      cleanups.push(() => badgeObserver.disconnect());
      syncBadge();
    }

    /* 4. localization selects ------------------------------------------- */
    const selects = DV.queryAll(header, 'select[data-dv-locale-select]');
    const onLocaleChange = (event) => {
      const { form } = event.currentTarget;
      if (!form) return;
      if (typeof form.requestSubmit === 'function') form.requestSubmit();
      else form.submit();
    };
    selects.forEach((select) => select.addEventListener('change', onLocaleChange));
    cleanups.push(() => selects.forEach((select) => select.removeEventListener('change', onLocaleChange)));

    return () => {
      if (frame) cancelAnimationFrame(frame);
      cleanups.forEach((fn) => fn());
      header.classList.remove('dv-header--no-shop', 'dv-header--collapsed');
      html.style.removeProperty('--header-height');
    };
  });
})();
